import { Component, inject, Input, input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { catchError, map, Observable, of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';
import { BaseModalComponent } from '@card-software/library';
import { AuthService } from '@card-software/nexus-utilities';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { ConsumptionWarehouseLocation, RecipePreviewItem } from '@app/models/dyeing-dashboard/production-card-dyeing-cycle-recipe-values';
import { InventoryTypeRef } from '@app/models/item/enum/InventoryTypeRef';
import { ItemMaster, ItemMasterFilter } from '@app/models/item/item-master/item-master';
import { ConsumedItem, ConsumedItemsStatus, FinishingRoute, ProcessKey } from '@app/models/production/finishing-route';
import { FinishingRouteConsumptionRoles } from '@app/models/roles/roles-group';
import { ItemMasterProxyService } from '@app/proxies/item/item-master.proxy.service';
import { FinishingRouteProxyService } from '@app/proxies/production/finishing-route.proxy.service';
import { EnumService } from '@app/services/enum.service';
import { LocaleHelperService } from '@app/services/locale-helper.service';

export interface ConsumedItemResult {
  finishingRoute: FinishingRoute;
}

@Component({
  selector: 'app-consumed-item-modal',
  templateUrl: './consumed-item-modal.component.html',
  styleUrl: './consumed-item-modal.component.scss',
})
export class ConsumedItemModalComponent extends BaseModalComponent<ConsumedItemResult | null> implements OnInit {
  @Input() process!: ProcessKey;
  @Input() finishingRouteId!: string;
  @Input() finishingRouteNumber?: string;

  private readonly fb = inject(FormBuilder);
  private readonly itemMasterProxyService = inject(ItemMasterProxyService);
  private readonly finishingRouteProxyService = inject(FinishingRouteProxyService);
  private readonly enumService = inject(EnumService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly translateService = inject(TranslateService);
  public localeHelperService = inject(LocaleHelperService);

  inventoryTypeReference = input<string | undefined>(InventoryTypeRef.AuxiliaryRawMaterials);

  processName = '';
  consumedItemsForm!: FormGroup;
  itemsCache: Map<string, ItemMaster & { optionDisplayName: string }> = new Map();
  isLoadingItems = false;
  isSaving = false;
  canEdit = false;
  isCompleted = false;
  consumptionWarehouse: ConsumptionWarehouseLocation | null = null;
  itemEnrichmentCache = new Map<string, RecipePreviewItem>();

  unitOfMeasureOptions = Object.entries(this.enumService.getEnum('UnitOfMeasure', 'itemEnums')?.values || {}).map(([key, label]) => ({
    label: label,
    value: parseInt(key),
  }));

  ngOnInit(): void {
    this.processName = this.process?.processName?.en || this.process?.processId || 'Process';
    this.isCompleted = this.process?.consumedItemsStatus === ConsumedItemsStatus.Completed;
    this.canEdit = AuthService.hasRole(FinishingRouteConsumptionRoles.FinishingRouteConsumptionAdmin);

    this.consumedItemsForm = this.fb.group({
      items: this.fb.array([]),
    });

    if (this.isCompleted || !this.canEdit) {
      this.consumedItemsForm.disable();
    }

    this.finishingRouteProxyService.getConsumptionWarehouse().subscribe({
      next: (response) => {
        if (response?.data) {
          this.consumptionWarehouse = response.data;
        }
      },
    });

    this.loadExistingItems();
  }

  private loadExistingItems(): void {
    if (this.process?.consumedItems && this.process.consumedItems.length > 0) {
      this.process.consumedItems.forEach((item) => {
        this.items.push(this.createItemFormGroup(item));
      });

      this.loadItemDescriptions(this.process.consumedItems.map((i) => i.itemCode));
      this.refreshEnrichment();
    } else if (this.canEdit && !this.isCompleted) {
      this.items.push(this.createItemFormGroup(null));
    }
  }

  private loadItemDescriptions(itemCodes: string[]): void {
    if (itemCodes.length === 0) return;

    this.isLoadingItems = true;

    const uniqueItemCodes = [...new Set(itemCodes)];
    let completedRequests = 0;

    uniqueItemCodes.forEach((itemCode) => {
      this.itemMasterProxyService
        .search({ itemCode })
        .pipe(
          catchError(() => {
            completedRequests++;
            if (completedRequests === uniqueItemCodes.length) {
              this.isLoadingItems = false;
            }
            return of(null);
          }),
        )
        .subscribe((response) => {
          completedRequests++;

          if (response?.success && response.data && response.data.items && response.data.items.length > 0) {
            const item = response.data?.items[0];
            this.itemsCache.set(item.itemCode, {
              ...item,
              optionDisplayName: this.localeHelperService.getLocalizedValue(item.itemDisplayName),
            });

            this.updateItemCodeDisplayValue(item.itemCode);
          }

          if (completedRequests === uniqueItemCodes.length) {
            this.isLoadingItems = false;
          }
        });
    });
  }

  private updateItemCodeDisplayValue(itemCode: string): void {
    const itemMaster = this.itemsCache.get(itemCode);
    if (!itemMaster) return;

    this.items.controls.forEach((control) => {
      const currentItemCode = control.get('itemCode')?.value;
      if (currentItemCode === itemCode) {
        control.patchValue(
          {
            itemCode: itemMaster,
          },
          { emitEvent: false },
        );
      }
    });
  }

  get items(): FormArray {
    return this.consumedItemsForm.get('items') as FormArray;
  }

  createItemFormGroup(consumedItem: ConsumedItem | null): FormGroup {
    return this.fb.group({
      itemCode: [consumedItem?.itemCode || '', [Validators.required, Validators.minLength(1)]],
      quantity: [consumedItem?.quantity || null, [Validators.required, Validators.min(0.01)]],
      unitOfMeasure: [{ value: consumedItem?.unitOfMeasure, disabled: true }, Validators.required],
    });
  }

  addItem(): void {
    this.items.push(this.createItemFormGroup(null));
  }

  removeItem(index: number): void {
    if (this.items.length > 1) {
      this.items.removeAt(index);
    }
  }

  public fetchItems = (query: string): Observable<ItemMaster[]> => {
    const searchCommand: ItemMasterFilter = {
      inventoryTypeReference: this.inventoryTypeReference(),
      name: query,
    };

    return this.itemMasterProxyService.search(searchCommand).pipe(
      map((response) =>
        (response?.data?.items ?? []).map((data) => ({
          ...data,
          optionDisplayName: this.localeHelperService.getLocalizedValue(data.itemDisplayName).trim(),
        })),
      ),
    );
  };

  onItemSelect(event: { value: ItemMaster }, index: number): void {
    const itemControl = this.items.at(index);
    const selectedItem = event.value;

    if (itemControl && selectedItem) {
      itemControl.patchValue({
        itemCode: selectedItem.itemCode,
        unitOfMeasure: selectedItem.unitOfMeasure,
      });
      this.refreshEnrichment();
    }
  }

  private extractItemCodeValue(raw: string | ItemMaster | null | undefined): string {
    if (!raw) return '';
    if (typeof raw === 'string') return raw.trim();
    return raw.itemCode?.trim() || '';
  }

  private refreshEnrichment(): void {
    const itemCodes = this.items.controls.map((fg) => this.extractItemCodeValue(fg.get('itemCode')?.value)).filter((code) => !!code);

    const uniqueCodes = [...new Set(itemCodes)];
    if (uniqueCodes.length === 0) return;

    this.finishingRouteProxyService.getConsumedItemsEnrichment(uniqueCodes).subscribe({
      next: (response) => {
        response?.data?.forEach((item) => this.itemEnrichmentCache.set(item.itemCode, item));
      },
    });
  }

  getCurrentInventory(rawItemCode: string | ItemMaster | null | undefined): number | undefined {
    const code = this.extractItemCodeValue(rawItemCode);
    return code ? this.itemEnrichmentCache.get(code)?.currentInventory : undefined;
  }

  getConsumptionWarehouseName(): string {
    if (!this.consumptionWarehouse) return '';
    return this.localeHelperService.getLocalizedValue(this.consumptionWarehouse.name) || this.consumptionWarehouse.code;
  }

  getConsumptionNoticeMessage(): string {
    return this.translateService.instant('Recipe_Values_Consumption_Notice', { warehouse: this.getConsumptionWarehouseName() });
  }

  getInsufficientInventoryItems(): { itemCode: string; itemDescription: string; required: number; available: number }[] {
    return this.items.controls
      .map((fg) => {
        const code = this.extractItemCodeValue(fg.get('itemCode')?.value);
        if (!code) return null;

        const enrichment = this.itemEnrichmentCache.get(code);
        const required = fg.get('quantity')?.value ?? 0;
        const available = enrichment?.currentInventory ?? 0;
        return { itemCode: code, itemDescription: enrichment?.itemDescription || code, required, available };
      })
      .filter((v): v is { itemCode: string; itemDescription: string; required: number; available: number } => v !== null)
      .filter((v) => v.required - v.available > 0.00001);
  }

  hasInsufficientInventory(): boolean {
    return this.getInsufficientInventoryItems().length > 0;
  }

  private buildConsumedItems(): ConsumedItem[] {
    return this.items.getRawValue().map((item: { itemCode: string | ItemMaster; quantity: number; unitOfMeasure: number }) => ({
      itemCode: this.extractItemCodeValue(item.itemCode),
      quantity: item.quantity,
      unitOfMeasure: item.unitOfMeasure,
    }));
  }

  saveDraft(): void {
    if (this.consumedItemsForm.invalid) {
      this.consumedItemsForm.markAllAsTouched();
      return;
    }

    this.isSaving = true;
    this.finishingRouteProxyService.addConsumedItems(this.finishingRouteId, this.process.lineId, this.buildConsumedItems()).subscribe({
      next: (response) => {
        this.isSaving = false;
        if (response?.data) {
          GlobalMessageUtil.success('Success', 'Consumed items saved successfully');
          this.close({ action: 'save', data: { finishingRoute: response.data } });
        }
      },
      error: () => {
        this.isSaving = false;
      },
    });
  }

  markAsComplete(): void {
    if (this.consumedItemsForm.invalid) {
      this.consumedItemsForm.markAllAsTouched();
      return;
    }

    this.confirmationService.confirm({
      message: this.translateService.instant('FinishingRoute_Consumption_Confirm_Message'),
      header: this.translateService.instant('Recipe_Values_Confirm_Header'),
      key: 'ci-dialog',
      acceptLabel: this.translateService.instant('Recipe_Values_Confirm_Accept'),
      rejectLabel: this.translateService.instant('Recipe_Values_Confirm_Reject'),
      accept: () => this.executeMarkAsComplete(),
    });
  }

  private executeMarkAsComplete(): void {
    this.isSaving = true;
    this.finishingRouteProxyService.addConsumedItems(this.finishingRouteId, this.process.lineId, this.buildConsumedItems()).subscribe({
      next: () => {
        this.finishingRouteProxyService.completeConsumedItems(this.finishingRouteId, this.process.lineId).subscribe({
          next: (response) => {
            this.isSaving = false;
            if (response?.data) {
              GlobalMessageUtil.success('Success', 'Consumed items marked as complete');
              this.close({ action: 'complete', data: { finishingRoute: response.data } });
            }
          },
          error: () => {
            this.isSaving = false;
          },
        });
      },
      error: () => {
        this.isSaving = false;
      },
    });
  }

  onClose(): void {
    this.close({ action: 'close', data: null });
  }

  isFieldInvalid(index: number, fieldName: string): boolean {
    const field = this.items.at(index).get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }
}
